import { Cookie, ExternalLink, ShieldCheck } from "lucide-react";
import Footer from "../components/Footer";
import { Navbar } from "../components/Navbar";

const providerLinkClass =
  "inline-flex items-center gap-1 text-[#044958] underline decoration-[#044958]/30 underline-offset-2 transition-colors hover:text-[#bf0908]";

const CookiePolicy = () => {
  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-linear-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 pt-38 pb-12 sm:px-6 sm:pt-42 sm:pb-16 lg:px-8">
        <header className="mb-8 text-center sm:mb-10">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-br from-[#044958] to-[#bf0908] shadow-lg sm:h-16 sm:w-16">
            <Cookie
              className="h-7 w-7 text-white sm:h-8 sm:w-8"
              aria-hidden="true"
            />
          </div>
          <h1 className="mb-3 text-3xl font-bold text-gray-900 sm:text-4xl">
            Cookie Policy
          </h1>
          <p className="text-gray-600">Last updated: September 19, 2026</p>
        </header>

        <article className="space-y-8 rounded-2xl bg-white p-5 text-gray-700 shadow-xl sm:p-8 lg:p-12">
          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              1. About this policy
            </h2>
            <p className="leading-relaxed">
              This Cookie Policy explains how Global Infra Projects ("we", "us",
              or "our") uses cookies and similar technologies on this website.
              It also explains what Google Analytics 4 and Microsoft Clarity
              collect, why we use them, how long related information may be
              retained, and the controls available through your browser and the
              providers.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              2. What cookies are
            </h2>
            <p className="leading-relaxed">
              Cookies are small text files stored by a browser. Similar
              technologies, including local identifiers, pixels, scripts, and
              browser storage, can recognize a browser or device and help
              measure how a website is used. First-party cookies are set for
              this website; third-party cookies are set or accessed by another
              service provider.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              3. How analytics operates
            </h2>
            <p className="leading-relaxed">
              On the production website, Google Analytics 4 and Microsoft
              Clarity initialize automatically when a page opens. They also
              receive page-view information as you move between pages. We send
              only the page path for these route events and exclude URL query
              strings and fragments. Analytics is disabled in our local
              development environment. An informational cookie notice appears
              once per tab session and automatically hides after three seconds.
              We do not maintain a cookie-preference setting on this website.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              4. Google Analytics 4
            </h2>
            <p className="mb-3 leading-relaxed">
              Google Analytics 4 (GA4), provided by Google LLC, helps us
              understand visits, navigation, device and browser characteristics,
              approximate geography, traffic sources, engagement, and website
              performance. We use this information to improve content,
              usability, and technical performance.
            </p>
            <p className="mb-3 leading-relaxed">
              We configure the Google tag with advertising storage, advertising
              user data, ad personalization, Google Signals, and
              ad-personalization signals disabled. We do not intentionally send
              names, email addresses, telephone numbers, form contents, URL
              query strings, or other direct identifiers in our page-view
              events.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-150 border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-900">
                    <th className="border border-gray-200 px-3 py-2">Cookie</th>
                    <th className="border border-gray-200 px-3 py-2">
                      Purpose
                    </th>
                    <th className="border border-gray-200 px-3 py-2">
                      Typical retention
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>_ga</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Distinguishes users.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Up to 2 years
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>_ga_&lt;container-id&gt;</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Maintains session state.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Up to 2 years
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              5. Microsoft Clarity
            </h2>
            <p className="mb-3 leading-relaxed">
              Microsoft Clarity, provided by Microsoft Corporation, helps us
              understand how visitors interact with pages through aggregated
              interaction metrics, heatmaps, diagnostics, and session
              recordings. Clarity can collect page and device information and
              interaction events such as clicks, scrolling, pointer movement,
              page visibility, and layout changes. Sensitive form content is
              intended to be masked by Clarity.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-150 border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-900">
                    <th className="border border-gray-200 px-3 py-2">Cookie</th>
                    <th className="border border-gray-200 px-3 py-2">
                      Purpose
                    </th>
                    <th className="border border-gray-200 px-3 py-2">Type</th>
                    <th className="border border-gray-200 px-3 py-2">
                      Retention / lifetime
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>_clck</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Persists a pseudonymous Clarity user ID and preferences
                      for this site.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      First-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      1 year
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>_clsk</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Connects page views into a Clarity session recording.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      First-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">1 day</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>CLID</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Records when Clarity first saw a browser on a site using
                      Clarity.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Third-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      1 year
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>ANONCHK</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Indicates whether MUID is transferred to ANID, a cookie
                      used for advertising. Clarity does not use ANID, so this
                      is set to 0.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Third-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      10 minutes
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>MR</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Indicates whether to refresh MUID.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Third-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      7 days
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>MUID</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Identifies unique web browsers visiting Microsoft sites
                      for advertising, site analytics, and operational
                      purposes.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Third-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      390 days
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">
                      <code>SM</code>
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Synchronizes MUID across Microsoft domains.
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Third-party
                    </td>
                    <td className="border border-gray-200 px-3 py-2">
                      Browser session
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              6. Retention
            </h2>
            <p className="leading-relaxed">
              Cookie lifetimes describe how long a cookie may remain in a
              browser; they are separate from the retention of analytics data
              on provider servers. GA4 cookies normally remain for up to two
              years, subject to browser limits and deletion by the user. The
              Microsoft Clarity cookie lifetimes are listed above and may be
              changed by Microsoft. Microsoft states that standard Clarity
              playback data is retained for 30 days, while click data,
              heatmaps, and labeled or favorited sessions may be retained for
              up to 9 months. Our own access to analytics information is
              limited to what the providers retain and make available, unless
              a longer period is required by law.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              7. Browser and provider controls
            </h2>
            <p className="mb-3 leading-relaxed">
              You can block or delete cookies, restrict third-party cookies,
              clear site data, enable tracking protection, or use
              private-browsing features through your browser. Blocking analytics
              scripts or cookies may reduce measurement accuracy but should not
              prevent access to the website’s core content. Because this site
              has no cookie-preferences control, browser and provider controls
              are the available technical controls on the site.
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Use your browser’s privacy or site-data settings to view, block,
                or delete cookies.
              </li>
              <li>
                Use a reputable content blocker or tracking-protection feature
                to block analytics requests.
              </li>
              <li>
                Install the{" "}
                <a
                  className={providerLinkClass}
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Analytics Opt-out Browser Add-on{" "}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                .
              </li>
              <li>
                Review Microsoft’s{" "}
                <a
                  className={providerLinkClass}
                  href="https://account.microsoft.com/privacy/ad-settings/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  privacy and advertising controls{" "}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              8. Third-party providers and transfers
            </h2>
            <p className="mb-3 leading-relaxed">
              Google and Microsoft process analytics information under their own
              terms and privacy policies and may process data in countries
              outside your own. Their safeguards and legal obligations govern
              those transfers. Their documentation may change independently of
              this policy.
            </p>
            <div className="flex flex-col items-start gap-2">
              <a
                className={providerLinkClass}
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy{" "}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <a
                className={providerLinkClass}
                href="https://privacy.microsoft.com/privacystatement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Privacy Statement{" "}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              9. Your privacy rights
            </h2>
            <p className="leading-relaxed">
              Depending on where you live, you may have rights to request
              access, correction, deletion, restriction, portability, or
              objection regarding personal data, and to lodge a complaint with a
              data-protection authority. To make a request or ask a question
              about our use of analytics, email{" "}
              <a
                className={providerLinkClass}
                href="mailto:info@theglobalinfra.co"
              >
                info@theglobalinfra.co
              </a>
              . We may need to verify your request and may be unable to identify
              data held only under a pseudonymous analytics identifier.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">
              10. Changes to this policy
            </h2>
            <p className="leading-relaxed">
              We may update this Cookie Policy when our practices, providers, or
              legal requirements change. The date at the top shows the latest
              revision. Please review this page periodically.
            </p>
          </section>

          <div className="flex items-start gap-3 border-t border-gray-200 pt-6 text-sm text-gray-600">
            <ShieldCheck
              className="mt-0.5 h-5 w-5 shrink-0 text-[#044958]"
              aria-hidden="true"
            />
            <p>
              Advertising-related Google storage, personalization, user data,
              and Signals remain disabled in this website’s analytics
              configuration.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicy;
